import { isRepoProductFields, type CreateRepoProduct } from '@src/types/OTC';
import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { logger, runValidationV1WithResult, type OTCRepoProductTemplate } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { BaseProductDefinition, type CreateProductParams } from './BaseProductDefinition';
import { repoProductBuilderSchema, type ValidatedRepoRowData } from './RepoProductBuilderSliceStrategy';

export class RepoProductDefinition extends BaseProductDefinition {
  private otcRepoProductTemplatesByDisplaySymbol: Map<string, OTCRepoProductTemplate> = new Map();

  updateReferenceData(otcRepoProductTemplatesByDisplaySymbol: Map<string, OTCRepoProductTemplate>) {
    this.otcRepoProductTemplatesByDisplaySymbol = otcRepoProductTemplatesByDisplaySymbol;
  }
  buildPostBody(row: ValidatedRepoRowData): CreateRepoProduct | undefined {
    const validationRun = runValidationV1WithResult(repoProductBuilderSchema, row);
    if (validationRun.result === 'failure') {
      return;
    }

    const { baseAsset, collateralAsset, otcFields, uniqueSuffix } = validationRun.validatedData;

    return {
      ProductType: 'OTC',
      SubProductType: 'OTCRepo',
      RequestType: 'OTC',
      Market: 'internal',
      ProductDetails: {
        TermType: otcFields.termType,
        UnitRepoAmount: otcFields.unitRepoAmount,
        RepoAsset: collateralAsset,
        BorrowedAsset: baseAsset,
        DayCountConvention: otcFields.dayCountConvention,
        ...(otcFields.termType === 'Fixed' && otcFields.term && { Term: otcFields.term }),
        ...(uniqueSuffix && { UniqueSuffix: uniqueSuffix }),
      },
    };
  }

  getProductDetailsForComparison(row: DealProductBuilderRow): CreateRepoProduct | null {
    if (!row.otcFields || !row.baseAsset || !isRepoProductFields(row.otcFields)) {
      return null;
    }

    const validation = runValidationV1WithResult(repoProductBuilderSchema, row);
    if (validation.result === 'failure') {
      throw new Error('Product builder validation failed');
    }

    return this.buildPostBody(validation.validatedData) ?? null;
  }

  checkForUniqueness(
    rowData: DealProductBuilderRow & { product: 'repo' }
  ): 'notReady' | 'uncreated' | 'conflict' | 'alreadyCreated' {
    const validation = runValidationV1WithResult(repoProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      return 'notReady';
    }

    const postBody = this.buildPostBody(validation.validatedData);
    if (!postBody) {
      return 'notReady';
    }

    const symbol =
      validation.validatedData.productTemplate +
      (postBody.ProductDetails.UniqueSuffix ? `-${postBody.ProductDetails.UniqueSuffix}` : '');

    const existingSecurity = this.otcRepoProductTemplatesByDisplaySymbol.get(symbol);
    if (!existingSecurity) {
      return 'uncreated';
    }

    if (!isEqual(postBody.ProductDetails, existingSecurity.ProductDetails)) {
      return 'conflict';
    }

    return 'alreadyCreated';
  }

  async createProduct({
    rowData,
    createCustomInstrument,
    onSuccess,
    onError,
  }: CreateProductParams & {
    rowData: DealProductBuilderRow & { product: 'repo' };
  }): Promise<{ symbol: string } | undefined> {
    const validation = runValidationV1WithResult(repoProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      onError('Validation failed on Repo product');
      return;
    }

    try {
      this.checkForUniqueness(rowData);
      const validatedData = validation.validatedData;

      const repo = this.buildPostBody(validatedData);
      if (!repo) {
        onError('Failed to build repo product body');
        return;
      }

      const rows = await createCustomInstrument(repo);
      onSuccess('Repo product created successfully');
      logger.info('Repo product created successfully', { data: rows });

      return { symbol: rows[0]?.Symbol ?? '' };
    } catch (error) {
      if (error instanceof Error && error.name === 'ValidationError') {
        onError(`Validation failed: ${error.message}`);
      } else {
        const serverMessage = this.getServerErrorMessage(error);
        onError(serverMessage ? `Error creating repo product: ${serverMessage}` : 'Error creating repo product');
      }
      logger.error(new Error('Error creating repo product'), {
        extra: {
          input: { rowData },
          message: error instanceof Error ? error.message : 'Unknown error',
          error,
        },
      });
      return;
    }
  }
}
